/**
 * UniversalScanner — Phase D
 * One camera-driven QR check-in that routes to the correct scope automatically.
 * Uses /api/scope-reg/check-in so event, school, church, ministry, campaign, org
 * registrations all resolve from the same screen.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { DashboardLayout } from '@/components/layouts';
import { getToken } from '@/lib/authToken';
import {
  QrCode, Camera, Keyboard, CheckCircle2, XCircle, Clock, ArrowLeft,
  Users, Building2, Church, Calendar, Heart, Layers,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPE_META = {
  event:    { icon: Calendar,   label: 'Event',    color: 'bg-indigo-500/15 text-indigo-700' },
  school:   { icon: Building2,  label: 'School',   color: 'bg-violet-500/15 text-violet-700' },
  church:   { icon: Church,     label: 'Church',   color: 'bg-cyan-500/15 text-cyan-700' },
  ministry: { icon: Heart,      label: 'Ministry', color: 'bg-rose-500/15 text-rose-700' },
  campaign: { icon: Layers,     label: 'Campaign', color: 'bg-emerald-500/15 text-emerald-700' },
  org:      { icon: Users,      label: 'Org',      color: 'bg-slate-500/15 text-slate-700' },
};

const ScopeBadge = ({ scope_type, scope_label }) => {
  const meta = SCOPE_META[scope_type] || SCOPE_META.org;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${meta.color}`} data-testid={`scope-badge-${scope_type}`}>
      <Icon className="h-3.5 w-3.5" />
      {meta.label}{scope_label ? ` · ${scope_label}` : ''}
    </span>
  );
};

const ResultCard = ({ result }) => {
  if (!result) return null;
  const ok = result.ok;
  return (
    <Card className={`border-2 ${ok ? 'border-emerald-400 bg-emerald-50/60' : 'border-rose-400 bg-rose-50/60'}`} data-testid="scan-result-card">
      <CardContent className="py-5">
        <div className="flex items-start gap-3">
          {ok ? (
            <CheckCircle2 className="h-7 w-7 text-emerald-600 shrink-0" />
          ) : (
            <XCircle className="h-7 w-7 text-rose-600 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">
              {ok ? (result.already_checked_in ? 'Already checked in' : 'Checked in') : 'Check-in failed'}
            </div>
            {result.full_name && (
              <div className="text-base font-bold truncate">{result.full_name}</div>
            )}
            {result.email && <div className="text-xs text-slate-600 truncate">{result.email}</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              {result.scope_type && <ScopeBadge scope_type={result.scope_type} scope_label={result.scope_label} />}
              {result.category && <Badge variant="outline" className="text-[11px]">{result.category}</Badge>}
            </div>
            {result.error && <div className="mt-2 text-xs text-rose-700">{result.error}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const UniversalScanner = () => {
  const navigate = useNavigate();
  const [useCamera, setUseCamera] = useState(true);
  const [manualCode, setManualCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({ ok: 0, fail: 0 });
  const lastScanRef = useRef({ code: '', at: 0 });

  const checkIn = useCallback(async (qr_code) => {
    if (!qr_code) return;
    // Debounce: ignore same code within 3s
    const now = Date.now();
    if (lastScanRef.current.code === qr_code && now - lastScanRef.current.at < 3000) return;
    lastScanRef.current = { code: qr_code, at: now };

    setBusy(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/scope-reg/check-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ qr_code }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const r = {
          ok: true,
          already_checked_in: body.already_checked_in === true,
          full_name: body.full_name,
          email: body.email,
          scope_type: body.scope_type,
          scope_label: body.scope_label,
          category: body.category,
          qr: qr_code,
        };
        setResult(r);
        setRecent((s) => [r, ...s].slice(0, 8));
        setStats((s) => ({ ...s, ok: s.ok + 1 }));
        toast.success(r.already_checked_in ? `Already checked in · ${body.full_name}` : `Checked in · ${body.full_name}`);
      } else {
        const r = {
          ok: false,
          error: body.detail || 'Unknown error',
          qr: qr_code,
        };
        setResult(r);
        setRecent((s) => [r, ...s].slice(0, 8));
        setStats((s) => ({ ...s, fail: s.fail + 1 }));
        toast.error(r.error);
      }
    } catch (e) {
      const r = { ok: false, error: 'Network error', qr: qr_code };
      setResult(r);
      setRecent((s) => [r, ...s].slice(0, 8));
      setStats((s) => ({ ...s, fail: s.fail + 1 }));
      toast.error('Network error');
    } finally {
      setBusy(false);
    }
  }, []);

  const onCameraDetected = useCallback((codes) => {
    if (!codes || codes.length === 0) return;
    const raw = codes[0]?.rawValue || codes[0]?.text || codes[0];
    if (typeof raw === 'string' && raw.trim()) checkIn(raw.trim());
  }, [checkIn]);

  const submitManual = useCallback((e) => {
    e?.preventDefault?.();
    const v = (manualCode || '').trim();
    if (!v) return;
    checkIn(v);
    setManualCode('');
  }, [manualCode, checkIn]);

  // Auto-focus manual input when camera is off
  const manualRef = useRef(null);
  useEffect(() => {
    if (!useCamera && manualRef.current) manualRef.current.focus();
  }, [useCamera]);

  return (
    <DashboardLayout title="Universal Scanner">
      <div className="space-y-5" data-testid="universal-scanner-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <QrCode className="h-5 w-5 text-cyan-600" /> Universal Check-in
              </h1>
              <p className="text-xs text-slate-500">One scanner for every scope — events, schools, churches, ministries, campaigns.</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {useCamera ? <Camera className="h-4 w-4 text-slate-500" /> : <Keyboard className="h-4 w-4 text-slate-500" />}
              <Label htmlFor="use-camera" className="text-xs font-medium">Camera</Label>
              <Switch id="use-camera" checked={useCamera} onCheckedChange={setUseCamera} data-testid="toggle-camera-switch" />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Badge className="bg-emerald-100 text-emerald-700" data-testid="stat-ok">OK {stats.ok}</Badge>
              <Badge className="bg-rose-100 text-rose-700" data-testid="stat-fail">Fail {stats.fail}</Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            {useCamera ? (
              <Card data-testid="camera-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Point camera at QR code</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg overflow-hidden border bg-black">
                    <Scanner
                      onScan={onCameraDetected}
                      onError={() => { /* silent */ }}
                      constraints={{ facingMode: 'environment' }}
                      styles={{ container: { width: '100%', aspectRatio: '1 / 1' } }}
                    />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="manual-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Enter QR code manually</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submitManual} className="flex gap-2">
                    <Input
                      ref={manualRef}
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      placeholder="Paste or type QR code…"
                      autoFocus
                      data-testid="manual-qr-input"
                    />
                    <Button type="submit" disabled={busy || !manualCode.trim()} data-testid="manual-submit-btn">Check in</Button>
                  </form>
                  <p className="text-[11px] text-slate-500 mt-2">USB barcode scanners work here too — they type and press Enter.</p>
                </CardContent>
              </Card>
            )}

            <ResultCard result={result} />
          </div>

          <div className="lg:col-span-1">
            <Card data-testid="recent-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4" /> Recent scans</CardTitle>
              </CardHeader>
              <CardContent>
                {recent.length === 0 ? (
                  <div className="text-xs text-slate-500 py-6 text-center">No scans yet</div>
                ) : (
                  <ul className="space-y-2">
                    {recent.map((r, i) => (
                      <li key={`${r.qr}-${i}`} className="flex items-start gap-2 text-xs border-b last:border-0 pb-2 last:pb-0" data-testid={`recent-item-${i}`}>
                        {r.ok ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />
                        ) : (
                          <XCircle className="h-4 w-4 text-rose-600 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{r.full_name || r.qr}</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {r.scope_type && <ScopeBadge scope_type={r.scope_type} scope_label={r.scope_label} />}
                            {r.error && <span className="text-rose-600">{r.error}</span>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UniversalScanner;
